/**
 * EPIC-046 part 2 phase A — reusable avatar primitive.
 *
 * Mirrors the inline pattern that lived in /u/[username]/page.tsx
 * (rounded image with a brand-tinted initials fallback). Centralized
 * here so feed cards, the post read page, and any future surface
 * that needs a poster's avatar render identical hit-targets and
 * fall back identically when `url` is null or fails to load.
 *
 * Sizing is a flat number (px) so callers control density without
 * tailwind-size juggling. Tap target stays >=24px even at the
 * smallest mobile-feed size we use today (28).
 */
"use client";

import Link from "next/link";
import { useState } from "react";

interface AvatarProps {
  url: string | null | undefined;
  fallbackChar: string;
  size: number;
  alt?: string;
  /** Optional href — when set, the avatar wraps in a Link. */
  href?: string;
  className?: string;
}

export function Avatar({
  url,
  fallbackChar,
  size,
  alt = "",
  href,
  className = "",
}: AvatarProps) {
  const [imgFailed, setImgFailed] = useState(false);
  const initial = (fallbackChar || "?").charAt(0).toUpperCase();
  // Initials font-size scales with the avatar. 24px avatar → ~12px char.
  const fontSize = Math.max(10, Math.round(size * 0.5));

  const sharedStyle: React.CSSProperties = {
    width: size,
    height: size,
    minWidth: size,
  };

  const inner =
    url && !imgFailed ? (
      // eslint-disable-next-line @next/next/no-img-element
      <img
        src={url}
        alt={alt}
        width={size}
        height={size}
        loading="lazy"
        className={`flex-shrink-0 rounded-full border border-[var(--color-border)] object-cover ${className}`}
        style={sharedStyle}
        onError={() => setImgFailed(true)}
      />
    ) : (
      <div
        className={`flex flex-shrink-0 items-center justify-center rounded-full font-bold ${className}`}
        style={{
          ...sharedStyle,
          background:
            "color-mix(in srgb, var(--color-brand) 14%, transparent)",
          color: "var(--color-brand)",
          fontSize,
        }}
        aria-hidden="true"
      >
        {initial}
      </div>
    );

  if (!href) return inner;
  return (
    <Link
      href={href}
      aria-label={alt || `Open profile`}
      className="block flex-shrink-0 transition-opacity hover:opacity-90"
      style={sharedStyle}
    >
      {inner}
    </Link>
  );
}
